import {
  OASISRadio,
  OASISYesNoCheckbox,
  OASISSelect,
  OASISText,
  OASISDate,
  OASISTime,
  OASISNumber,
  OASISMultiselect,
  OASISMultiselectOption,
  OASISRadioOption,
} from '../primitives';
import { layoutInlineStyle } from './layoutInlineStyle';
import type { LayoutNodeRenderContext } from './nodeRenderProps';
import { Box } from './nodes/Box';
import { Card } from './nodes/Card';
import { Conditional } from './nodes/Conditional';
import { DisabledWhenBlock } from './nodes/DisabledWhenBlock';
import { DisplayBlock } from './nodes/DisplayBlock';
import { Divider } from './nodes/Divider';
import { FieldRow } from './nodes/FieldRow';
import { Flex } from './nodes/Flex';
import { Grid } from './nodes/Grid';
import { Indent } from './nodes/Indent';
import { RepeatBlock } from './nodes/RepeatBlock';
import { SectionLabel } from './nodes/SectionLabel';
import { Stack } from './nodes/Stack';
import { StaticTextBlock } from './nodes/StaticTextBlock';
import { Table } from './nodes/Table';
import type { LayoutNode } from './types';

export function RenderNode({
  node,
  answers,
  repeatBindings,
}: LayoutNodeRenderContext & { node: LayoutNode }) {
  switch (node.type) {
    case 'card':
      return (
        <Card node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'stack':
      return (
        <Stack node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'grid':
      return (
        <Grid node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'flex':
      return (
        <Flex node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'box':
      return (
        <Box node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'indent':
      return (
        <Indent node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'divider':
      return <Divider node={node} />;
    case 'static':
      return (
        <StaticTextBlock
          text={node.text}
          as={node.as ?? 'div'}
          variant={node.variant}
          className={node.className}
          style={node.style}
        />
      );
    case 'text':
      return (
        <StaticTextBlock
          text={node.text}
          as={node.as ?? 'span'}
          variant={node.variant}
          className={node.className}
          style={node.style}
        />
      );
    case 'field-row':
      return (
        <FieldRow
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'sum':
      return null;
    case 'display':
      return <DisplayBlock node={node} />;
    case 'section-label':
      return <SectionLabel node={node} />;
    case 'table':
      return (
        <Table
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
          renderNode={RenderNode}
        />
      );
    case 'conditional':
      return (
        <Conditional
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'disabled-when':
      return (
        <DisabledWhenBlock
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'repeat':
      return (
        <RepeatBlock
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );

    case 'OASISRadio':
      return (
        <OASISRadio
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          inline={node.inline}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          groupClassName={node.groupClassName}
          optionRowClassName={node.optionRowClassName}
          optionLabelClassName={node.optionLabelClassName}
          controlClassName={node.controlClassName}
          itemClassName={node.itemClassName}
          orientation={node.orientation}
        />
      );
    case 'OASISYesNoCheckbox':
      return (
        <OASISYesNoCheckbox
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          optionRowClassName={node.optionRowClassName}
          controlClassName={node.controlClassName}
          checkboxClassName={node.checkboxClassName}
        />
      );
    case 'OASISSelect':
      return (
        <OASISSelect
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          triggerClassName={node.triggerClassName}
          placeholder={node.placeholder}
        />
      );
    case 'OASISText':
      return (
        <OASISText
          qid={node.qid}
          labelOverride={node.labelOverride}
          multiline={node.multiline}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISDate':
      return (
        <OASISDate
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISTime':
      return (
        <OASISTime
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISNumber':
      return (
        <OASISNumber
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          controlClassName={node.controlClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISMultiselect': {
      const hasCompound = node.children != null && node.children.length > 0;
      return (
        <OASISMultiselect
          qid={node.qid}
          labelOverride={node.labelOverride}
          hideLabel={node.hideLabel}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          checklistClassName={node.checklistClassName}
          checklistStyle={layoutInlineStyle(node.checklistStyle)}
          optionRowClassName={node.optionRowClassName}
          optionLabelClassName={node.optionLabelClassName}
          controlClassName={node.controlClassName}
          checkboxClassName={node.checkboxClassName}
          compoundContent={
            hasCompound ? (
              <RenderNodes
                nodes={node.children!}
                answers={answers}
                repeatBindings={repeatBindings}
              />
            ) : undefined
          }
        />
      );
    }
    case 'OASISMultiselectOption':
      return (
        <OASISMultiselectOption
          value={node.value}
          valueFrom={node.valueFrom}
          labelOverride={node.labelOverride}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          labelClassName={node.labelClassName}
          optionRowClassName={node.optionRowClassName}
          controlClassName={node.controlClassName}
          checkboxClassName={node.checkboxClassName}
        />
      );
    case 'radio-option':
      return (
        <OASISRadioOption
          qid={node.qid}
          value={node.value}
          className={node.className}
          style={layoutInlineStyle(node.style)}
          controlClassName={node.controlClassName}
          itemClassName={node.itemClassName}
        />
      );
  }
}

export function RenderNodes({
  nodes,
  answers,
  repeatBindings,
}: LayoutNodeRenderContext & { nodes: LayoutNode[] }) {
  return (
    <>
      {nodes.map((node, i) => (
        <RenderNode
          key={i}
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      ))}
    </>
  );
}
